import { Box, Text, VStack } from '@chakra-ui/react'
import { flow } from '@helo/utils'
import { Group } from '@visx/group'
import { Arc } from '@visx/shape'
import type { ReactNode } from 'react'
import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useChartColors } from '../../../components/charts/common/useChartColors'
import { useIsDark } from '../../../hooks/useIsDark'
import { Options } from '../../Options'
import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import type { ChartDataOutput } from '../ChartData'
import type { ChartLegendOutput } from '../ChartLegend'
import type { ChartSeriesOutput } from '../ChartSeries'
import type { ChartTooltipOutput } from '../ChartTooltip'
import { ChartTooltip } from '../ChartTooltip'
import { ChartTooltipConstants } from '../ChartTooltip/ChartTooltip.constants'
import { ChartTooltipUtils } from '../ChartTooltip/ChartTooltip.parts'
import { ChartPieConstants } from './ChartPie.constants'
import { type ChartPieOptions, chartPieOptions } from './ChartPie.options'
import {
  ChartPieInputParts,
  ChartPieOutputParts,
  ChartPieUtils,
} from './ChartPie.parts'

export { chartPieOptions } from './ChartPie.options'
export type { ChartPieOptions } from './ChartPie.options'

export type ChartPieInput = ChartPieOptions & {
  _series?: Options.State[]
}

export type ResolvedChartPieSlice = {
  key: string
  label: string
  value: number
  percent: number
  color: string
  highlightColor: string
  /** d3/VisX radians — 0 at 12 o'clock, clockwise. */
  startAngle: number
  endAngle: number
  midAngle: number
  isOther: boolean
  otherItems?: Array<{ name: string; value: number }>
  /** Tooltip / legend marker compatibility. */
  pointRadius: number
  lineWidth: number
  visualization: 'pie'
}

export type ChartPieSlice = {
  slices: ResolvedChartPieSlice[]
  total: number
  innerRadius: number
  outerRadius: number
  diameter: number
  showDataLabels: boolean
  showCenterTotal: boolean
  minLabelPct: number
  isDonut: boolean
  centerTotalCaption: string
  measureLabel?: string
  formatDisplayValue: (value: number) => string
  plotWidth: number
}

export type ChartPieOutput = {
  pie: ChartPieSlice
}

export interface ChartPieProps {
  renderConfig: Partial<ChartPieOutput> &
    Partial<ChartLegendOutput> &
    Partial<ChartTooltipOutput> &
    Partial<ChartDataOutput>
}

const chartPieSetup = () =>
  RenderPipeline.createFeatureSetup<
    ChartPieInput,
    Partial<ChartPieOutput>,
    ChartDataOutput & ChartSeriesOutput & Pick<QueryOutput, 'contentNode'>
  >(flow(ChartPieInputParts.setupInput(), ChartPieOutputParts.setPie()))

type ClientPointer = { clientX: number; clientY: number }

/** Dumb mark — renders pre-calculated slice arcs, labels, center total, and slice tooltip. */
const Slices = ({ renderConfig }: ChartPieProps): ReactNode => {
  const { pie, tooltip } = renderConfig
  const chartColors = useChartColors()
  const isDark = useIsDark()
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [pointer, setPointer] = useState<ClientPointer | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [adjusted, setAdjusted] = useState<{
    left: number
    top: number
  } | null>(null)

  const slices = pie?.slices ?? []
  const tooltipActive = tooltip?.isActive === true && tooltip.disabled !== true
  const buildPiePayload = tooltipActive ? tooltip?.buildPiePayload : undefined
  const activeSlice =
    activeKey == null
      ? null
      : (slices.find(slice => slice.key === activeKey) ?? null)
  const payload =
    activeSlice != null && buildPiePayload != null && pie != null
      ? buildPiePayload(activeSlice, pie)
      : null

  const draftLeft = pointer
    ? pointer.clientX + ChartTooltipConstants.cursorOffsetPx
    : 0
  const draftTop = pointer
    ? pointer.clientY + ChartTooltipConstants.cursorOffsetPx
    : 0

  useLayoutEffect(() => {
    if (payload == null || pointer == null) {
      setAdjusted(null)
      return
    }
    const el = panelRef.current
    if (el == null) return
    const rect = el.getBoundingClientRect()
    const next = ChartTooltipUtils.clampTooltipPosition(
      draftLeft,
      draftTop,
      rect.width,
      rect.height,
    )
    setAdjusted(prev =>
      prev?.left === next.left && prev?.top === next.top ? prev : next,
    )
  }, [payload, draftLeft, draftTop, pointer])

  if (pie == null || slices.length === 0) return null

  const {
    total,
    innerRadius,
    outerRadius,
    diameter,
    showDataLabels,
    showCenterTotal,
    minLabelPct,
    isDonut,
    centerTotalCaption,
    formatDisplayValue,
    plotWidth,
  } = pie

  const cx = plotWidth / 2
  const cy = diameter / 2

  const panel =
    payload != null && pointer != null ? (
      <ChartTooltip.Panel
        panelRef={panelRef}
        payload={payload}
        xLabel={payload.x}
        position="fixed"
        left={`${adjusted?.left ?? draftLeft}px`}
        top={`${adjusted?.top ?? draftTop}px`}
      />
    ) : null

  return (
    <Box
      flexShrink={0}
      w={`${plotWidth}px`}
      maxW="100%"
      h={`${diameter}px`}
      position="relative"
      data-color-mode={isDark ? 'dark' : 'light'}
      data-testid="chart-pie-plot"
    >
      <svg width={plotWidth} height={diameter} role="img">
        <Group top={cy} left={cx}>
          {slices.map(slice => (
            <Arc
              key={slice.key}
              data={slice}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              startAngle={slice.startAngle}
              endAngle={slice.endAngle}
              padAngle={0}
              cornerRadius={0}
              fill={slice.color}
              stroke={
                ChartPieConstants.sliceStrokeWidth > 0
                  ? chartColors.pieSliceStroke
                  : undefined
              }
              strokeWidth={
                ChartPieConstants.sliceStrokeWidth > 0
                  ? ChartPieConstants.sliceStrokeWidth
                  : undefined
              }
              onMouseEnter={event => {
                setActiveKey(slice.key)
                setPointer({
                  clientX: event.clientX,
                  clientY: event.clientY,
                })
              }}
              onMouseMove={event => {
                setActiveKey(slice.key)
                setPointer({
                  clientX: event.clientX,
                  clientY: event.clientY,
                })
              }}
              onMouseLeave={() => {
                setActiveKey(null)
                setPointer(null)
              }}
            />
          ))}
          {showDataLabels
            ? slices.map(slice => {
                if (slice.percent < minLabelPct) return null
                const { x, y } = ChartPieUtils.percentLabelPosition({
                  cx: 0,
                  cy: 0,
                  midAngle: slice.midAngle,
                  outerRadius,
                })
                return (
                  <text
                    key={`${slice.key}-label`}
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={chartColors.piePercentLabelFill}
                    fontSize="12px"
                    fontWeight="600"
                    fontFamily="Montserrat, sans-serif"
                    pointerEvents="none"
                  >
                    {Math.round(slice.percent)}%
                  </text>
                )
              })
            : null}
        </Group>
      </svg>
      {isDonut && showCenterTotal ? (
        <VStack
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          gap="3px"
          pointerEvents="none"
        >
          <Text
            color={chartColors.pieCenterTotalFill}
            fontSize="28px"
            fontWeight="600"
            lineHeight="1"
          >
            {formatDisplayValue(total)}
          </Text>
          <Text
            color={chartColors.pieCenterCaptionFill}
            fontSize="12px"
            fontWeight="500"
            lineHeight="1"
          >
            {centerTotalCaption}
          </Text>
        </VStack>
      ) : null}
      {panel != null ? createPortal(panel, document.body) : null}
    </Box>
  )
}

export const ChartPie = {
  options: chartPieOptions,
  Slices,
  setup: chartPieSetup,
}
